import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, ExternalLink, Lock } from "lucide-react";

import { ANALYTICS_EVENTS } from "analytics";
import { Button } from "components/common";
import { SimpleUpgradeModal } from "components/common/UpgradeModal";
import { useAnalytics } from "hooks/useAnalytics";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import {
  isMcpConnectorLicenseError,
  useMcpConnector,
  type McpConnectorClient,
  type McpConnectorCreated,
} from "hooks/workspace/useMcpConnector";
import { useMcpConnections } from "hooks/workspace/useMcpConnections";
import { Permissions } from "utils/permissions/permissionConstants";
import { ENVS } from "config";
import { AiAppMark, aiAppFromClientId, CURSOR_OAUTH_CLIENT_ID } from "../AiAppMark/AiAppMark";

type ConnectorState = "checking" | "unavailable" | "idle" | "created" | "waiting" | "connected";

type ClientConfig = {
  name: string;
  pitch: string;
  connectLabel: string;
  setupTitle: string;
  setupBody: string;
  setupAction: string;
  waitingBody: string;
  openAfterCreate: boolean;
};

const CLIENT_CONFIG: Record<McpConnectorClient, ClientConfig> = {
  claude: {
    name: "Claude",
    pitch: "Run breakdowns, schedules, and budgets from Claude.",
    connectLabel: "Connect Claude",
    setupTitle: "Add RivetAI in Claude",
    setupBody: "Claude opens with the connector details filled in. Approve it to finish.",
    setupAction: "Add in Claude",
    waitingBody: "Approve RivetAI in the Claude tab. This page updates after its first call.",
    openAfterCreate: true,
  },
  chatgpt: {
    name: "ChatGPT",
    pitch: "Use RivetAI as a custom MCP app in ChatGPT.",
    connectLabel: "Connect ChatGPT",
    setupTitle: "Add RivetAI in ChatGPT",
    setupBody:
      "Copy the server URL, then create a custom app in ChatGPT and choose no authentication.",
    setupAction: "Open ChatGPT",
    waitingBody:
      "In ChatGPT, enable developer mode, then go to Settings → Apps → Create and paste the server URL.",
    openAfterCreate: false,
  },
  cursor: {
    name: "Cursor",
    pitch: "Use RivetAI tools directly from Cursor Agent.",
    connectLabel: "Connect Cursor",
    setupTitle: "Install RivetAI in Cursor",
    setupBody: "Cursor opens an MCP install prompt with this connection already configured.",
    setupAction: "Install in Cursor",
    waitingBody: "Approve the MCP server in Cursor. This page updates after its first call.",
    openAfterCreate: true,
  },
};

export const McpConnectorCards = ({ workspaceId }: { workspaceId: string }) => {
  const { canWorkspace, isLoading } = useWorkspaceAuth(workspaceId);
  const oauth = Boolean(ENVS.mcpOAuthUrl);
  // OAuth connections are per person and bounded by their own role, so every
  // member may connect. Key-based connectors need API key permission.
  if (isLoading || (!oauth && !canWorkspace(Permissions.WORKSPACE_API_KEY_LIST))) return null;

  return (
    <section aria-labelledby="ai-connections-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="ai-connections-heading" className="text-base font-semibold text-foreground">
          Add RivetAI to an AI app
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {oauth
            ? "Every app uses the same address. You sign in to RivetAI and choose a workspace, so there are no keys to copy."
            : "Each app gets its own revocable key. Reconnecting one will not disconnect the others."}
        </p>
      </div>
      {ENVS.mcpOAuthUrl ? (
        <div className="max-w-xl">
          <CopyField label="Server URL" value={ENVS.mcpOAuthUrl} />
        </div>
      ) : null}
      {/* Three columns only when each card fits its actions on one row. */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <McpConnectorCard workspaceId={workspaceId} client="claude" variant="manage" />
        <McpConnectorCard workspaceId={workspaceId} client="chatgpt" variant="manage" />
        <McpConnectorCard workspaceId={workspaceId} client="cursor" variant="manage" />
      </div>
    </section>
  );
};

type McpConnectorCardProps = {
  workspaceId: string;
  client: McpConnectorClient;
  variant: "promo" | "manage";
  onDismiss?: () => void;
};

export const McpConnectorCard = (props: McpConnectorCardProps) =>
  ENVS.mcpOAuthUrl ? (
    <OAuthConnectorCard {...props} url={ENVS.mcpOAuthUrl} />
  ) : (
    <KeyConnectorCard {...props} />
  );

export const claudeAddUrl = (url: string) =>
  `https://claude.ai/customize/connectors?${new URLSearchParams({
    modal: "add-custom-connector",
    connectorName: "RivetAI",
    connectorUrl: url,
  }).toString()}`;

// Cursor only signs in through open client registration, which our server
// does not offer, so the link names the client the backend seeds for Cursor.
export const cursorInstallUrl = (url: string) =>
  `cursor://anysphere.cursor-deeplink/mcp/install?${new URLSearchParams({
    name: "RivetAI",
    config: btoa(JSON.stringify({ url, auth: { CLIENT_ID: CURSOR_OAUTH_CLIENT_ID } })),
  }).toString()}`;

const OAUTH_SETUP: Record<
  McpConnectorClient,
  { body: string; actionLabel?: string; action?: (url: string) => string }
> = {
  claude: {
    body: "Opens Claude with RivetAI filled in. Sign in and choose this workspace to finish.",
    actionLabel: "Add in Claude",
    action: claudeAddUrl,
  },
  // ChatGPT has no link that opens its app setup, so the action copies the URL.
  chatgpt: {
    body: "In ChatGPT, turn on developer mode in Settings, then Apps. Create an app with the server URL and OAuth sign-in.",
  },
  cursor: {
    body: "Opens Cursor's install prompt. Cursor asks you to sign in the first time it connects.",
    actionLabel: "Install in Cursor",
    action: cursorInstallUrl,
  },
};

/**
 * Universal URL setup. No credential is created or shown. On the Connections
 * page the section shows the server URL once; the promo card shows its own.
 * When the signed-in user already connected this app, the card says so.
 */
const OAuthConnectorCard = ({
  workspaceId,
  client,
  variant,
  onDismiss,
  url,
}: McpConnectorCardProps & { url: string }) => {
  const config = CLIENT_CONFIG[client];
  const setup = OAUTH_SETUP[client];
  const [open, setOpen] = useState(variant === "manage");
  const { connectionsQuery } = useMcpConnections(workspaceId, "mine");
  const connection = (connectionsQuery.data ?? []).find(
    (candidate) => aiAppFromClientId(candidate.client_id) === client
  );

  if (variant === "promo" && connection) return null;

  // No h-full: grid cells already stretch, and in the Projects page flex column
  // it would fill the whole page.
  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <AiAppMark client={client} />
        <p className="text-sm font-medium text-foreground">{config.name}</p>
        {connection ? <StatusBadge label="Connected" active /> : null}
      </div>
      <p className="mt-3 flex-1 text-sm text-muted-foreground">
        {open ? setup.body : config.pitch}
      </p>

      {open && variant === "promo" && (
        <div className="mt-4">
          <CopyField label="Server URL" value={url} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {open && setup.action && setup.actionLabel && (
          <Button asChild variant={connection ? "ghost" : "subtle"} size="sm">
            <a href={setup.action(url)} target="_blank" rel="noopener noreferrer">
              {connection ? "Add again" : setup.actionLabel}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          </Button>
        )}
        {open && !setup.action && (
          <CopyButton
            label="Copy server URL"
            ariaLabel="Copy server URL"
            value={url}
            variant={connection ? "ghost" : "subtle"}
            size="sm"
          />
        )}
        {open && client === "claude" && (
          <CopyButton
            label="Claude Code"
            value={`claude mcp add --transport http rivetai ${url}`}
          />
        )}
        {!open && (
          <Button type="button" variant="subtle" size="sm" onClick={() => setOpen(true)}>
            {config.connectLabel}
          </Button>
        )}
        {variant === "promo" && onDismiss && (
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss} className="ml-auto">
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
};

const KeyConnectorCard = ({ workspaceId, client, variant, onDismiss }: McpConnectorCardProps) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const config = CLIENT_CONFIG[client];
  const { statusQuery, createMutation, disconnectMutation, isCreating, isDisconnecting } =
    useMcpConnector(workspaceId, client);
  const [artifacts, setArtifacts] = useState<McpConnectorCreated | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const status = statusQuery.data;
  const isConnected = status?.connected === true;
  const hasFirstUse = Boolean(isConnected && status.first_used_at);
  // A failed first load is unknown, not "idle": Connect would create a new key.
  const state: ConnectorState =
    statusQuery.isLoading && status === undefined
      ? "checking"
      : statusQuery.isError && status === undefined
        ? "unavailable"
        : hasFirstUse
          ? "connected"
          : artifacts
            ? "created"
            : isConnected
              ? "waiting"
              : "idle";

  useEffect(() => {
    if (!hasFirstUse) return;
    setArtifacts(null);
    if (variant === "promo") onDismiss?.();
  }, [hasFirstUse, onDismiss, variant]);

  const handleConnect = async () => {
    try {
      const created = await createMutation.mutateAsync();
      setArtifacts(created);
      trackEvent(ANALYTICS_EVENTS.CONNECTOR_KEY_CREATED, {
        workspace_id: workspaceId,
        api_key_id: created.api_key_id,
        client,
      });
      if (config.openAfterCreate && created.install_url) {
        window.open(created.install_url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      if (isMcpConnectorLicenseError(error)) setShowUpgrade(true);
    }
  };

  const handleDisconnect = async () => {
    await disconnectMutation.mutateAsync();
    setArtifacts(null);
  };

  if (variant === "promo" && state === "connected") return null;
  if (state === "checking") return variant === "promo" ? null : <ConnectorSkeleton />;
  if (state === "unavailable") {
    return variant === "promo" ? null : (
      <div role="alert" className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <AiAppMark client={client} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{config.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">Couldn't check this connection</p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="badge"
            color="gray"
            size="sm"
            onClick={() => void statusQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const body = (() => {
    if (state === "created") return config.setupBody;
    if (state === "waiting") return config.waitingBody;
    if (state === "connected" && status?.connected) {
      return status.last_used_at
        ? `Last used ${new Date(status.last_used_at).toLocaleDateString()}.`
        : "Ready to use.";
    }
    return config.pitch;
  })();

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-4">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <AiAppMark client={client} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-foreground">
              {state === "created" ? config.setupTitle : config.name}
            </p>
            {state === "connected" && <StatusBadge label="Active" active />}
            {state === "waiting" && <StatusBadge label="Setup pending" />}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{body}</p>
        </div>
      </div>

      {state === "created" && artifacts && (
        <div className="mt-4 space-y-3 border-t border-border pt-3">
          <CopyField label="Server URL" value={artifacts.connector_url} />
          {artifacts.install_url && (
            <Button asChild variant="subtle" size="sm">
              <a href={artifacts.install_url} target="_blank" rel="noopener noreferrer">
                {config.setupAction}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            </Button>
          )}
          {artifacts.command && <CopyField label="Claude Code command" value={artifacts.command} />}
          {artifacts.admin_install_url && (
            <a
              href={artifacts.admin_install_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-xs text-muted-foreground hover:text-foreground"
            >
              Add for a Claude Team or Enterprise workspace
            </a>
          )}
          <SecurityNote />
        </div>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {state === "idle" && onDismiss && variant === "promo" && (
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
        {state === "idle" && (
          <Button
            type="button"
            variant="subtle"
            size="sm"
            onClick={() => void handleConnect()}
            disabled={isCreating}
            isLoading={isCreating}
          >
            {config.connectLabel}
          </Button>
        )}
        {state === "waiting" && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleConnect()}
            disabled={isCreating}
            isLoading={isCreating}
          >
            Regenerate setup
          </Button>
        )}
        {state === "connected" && variant === "manage" && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handleDisconnect()}
              disabled={isDisconnecting}
            >
              Disconnect
            </Button>
            <Button
              type="button"
              variant="subtle"
              size="sm"
              onClick={() => void handleConnect()}
              disabled={isCreating}
              isLoading={isCreating}
            >
              Regenerate
            </Button>
          </>
        )}
      </div>

      <SimpleUpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="License required"
        message="AI app connections need a Studio-licensed project or an active plan."
        actionText="View billing"
        onAction={() => navigate(`/workspaces/${workspaceId}/settings/billing`)}
      />
    </div>
  );
};

const StatusBadge = ({ label, active = false }: { label: string; active?: boolean }) => (
  <span
    className={
      active
        ? "inline-flex items-center gap-1 rounded-full border border-presence/40 px-2 py-0.5 text-[11px] font-medium text-presence"
        : "inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
    }
  >
    {active && <Check className="h-3 w-3" aria-hidden />}
    {label}
  </span>
);

const SecurityNote = () => (
  <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
    <Lock className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
    <p className="text-xs text-muted-foreground">
      This URL contains a workspace credential. Keep it private and regenerate it if shared.
    </p>
  </div>
);

export const useCopy = (value: string) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // The visible value remains selectable when clipboard access is unavailable.
    }
  };

  return { copied, copy };
};

export const CopyButton = ({
  label,
  value,
  ariaLabel,
  variant = "ghost",
  size = "xs",
}: {
  label: string;
  value: string;
  ariaLabel?: string;
  variant?: "ghost" | "subtle";
  size?: "xs" | "sm";
}) => {
  const { copied, copy } = useCopy(value);
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={() => void copy()}
      title={value}
      aria-label={ariaLabel ?? `Copy ${label} command`}
    >
      {copied ? <Check className="h-3 w-3 text-presence" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : label}
    </Button>
  );
};

export const CopyField = ({ label, value }: { label: string; value: string }) => {
  const { copied, copy } = useCopy(value);

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="mt-1.5 flex items-center gap-2 rounded-md border border-border bg-muted/30 py-1 pl-2.5 pr-1">
        <code
          className="min-w-0 flex-1 select-all truncate font-mono text-xs text-foreground"
          title={value}
        >
          {value}
        </code>
        <Button type="button" variant="ghost" size="sm" onClick={() => void copy()}>
          {copied ? <Check className="h-3 w-3 text-presence" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
};

const ConnectorSkeleton = () => (
  <div className="h-32 animate-pulse rounded-lg border border-border bg-card" aria-hidden />
);
